import { Box, Typography, Grid, alpha, useTheme, IconButton, Stack, Button, TextField, Collapse, LinearProgress, Divider, Skeleton, Paper, MenuItem, Select } from "@mui/material";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  TbListDetails, TbUsers, TbBolt, TbShieldCheck, TbSearch, TbHistory, TbAdjustmentsHorizontal, TbActivity, TbCloudComputing, TbChartPie, TbRefresh, TbFilterOff
} from "react-icons/tb";
import { LuPackage, LuLayers, LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight } from "react-icons/lu";
import { FaRecycle, FaIndustry } from "react-icons/fa";
import { MdHub } from "react-icons/md";

import { User } from "../../db_access/user";
import { Usage } from "../../db_access/usage";
import DonutChart from "../../components/chart/DonutChart";
import UsageGlobalView from "./UsageGlobalView";
import CheckBox from "../../components/input/CheckBox";
import DateSelector from "../../components/input/DateSelector";

import MetricCard from "../../app_components/MetricCard";
import AgGrid from "../../components/grid/AgGrid";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import RefreshButton from "../../components/button/RefreshButton";
import SearchBox from "../../components/input/SearchBox";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { leftAlignColDef, rightAlignColDef, defaultColDef } from "../../components/grid/columnDefinitions/columnDefinitions";

// ── Icon map for module sections (matches sidebar icons) ─────────────────────
const SECTION_ICON_MAP = {
  'Product': LuPackage,
  'Structure': FaIndustry,
  'Project': LuLayers,
  'Supply Chain': MdHub,
  'Reused Product': FaRecycle,
  'Partnership': MdHub,
};
const getModuleIcon = (sectionName) => SECTION_ICON_MAP[sectionName] || TbActivity;

const cellTextSx = (isDark) => ({
  fontFamily: GRAPH_PALETTE.font.family,
  fontSize: GRAPH_PALETTE.font.sizes.standard,
  fontWeight: GRAPH_PALETTE.font.weights.standard,
  color: isDark ? "#f1f5f9" : "#1e293b",
});

dayjs.extend(relativeTime);

const UsageCompany = ({ startDate, endDate, setStartDate, setEndDate, refresh, setGlobalStats }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const mc = ORG_PALETTE.metricCards;

  const [usages, setUsages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [colleagues, setColleagues] = useState([]);
  const [userSearchText, setUserSearchText] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  const [limitLoading, setLimitLoading] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [auditSearch, setAuditSearch] = useState("");
  const [globalView, setGlobalView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(10);

  const gridRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [usageRes, colleagueRes] = await Promise.all([
          Usage.getMineCompany(),
          User.get_my_colleagues()
        ]);
        const usageData = await usageRes.json();
        const colleagueData = await colleagueRes.json();

        setUsages(Array.isArray(usageData) ? usageData : []);
        setColleagues((Array.isArray(colleagueData) ? colleagueData : []).map(c => ({
          ...c,
          displayName: c.username || c.user_id || 'Anonymous User',
        })));
        setIsLoading(false);
      } catch (err) {
        console.error("Data fetch error:", err);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [refresh, localRefresh]);

  const timedUsages = useMemo(() => {
    return usages.filter(u => {
      const ts = dayjs(u.timestamp);
      return (ts.isAfter(startDate) || ts.isSame(startDate, 'day')) &&
        (ts.isBefore(endDate) || ts.isSame(endDate, 'day'));
    });
  }, [usages, startDate, endDate]);

  const filteredUsages = useMemo(() => {
    if (!selectedUser) return timedUsages;
    return timedUsages.filter(u => u.username === selectedUser);
  }, [timedUsages, selectedUser]);

  const totalPoints = useMemo(() => filteredUsages.reduce((sum, curr) => sum + (Number(curr.point) || 0), 0), [filteredUsages]);

  const sectionUsages = useMemo(() => {
    const sectionTotals = filteredUsages.reduce((acc, usage) => {
      acc[usage.section_name] = (acc[usage.section_name] || 0) + (Number(usage.point) || 0);
      return acc;
    }, {});
    return Object.entries(sectionTotals).map(([section, total], i) => ({ id: i, section_name: section, point: total }));
  }, [filteredUsages]);

  const userUsages = useMemo(() => {
    const userTotals = timedUsages.reduce((acc, usage) => {
      acc[usage.username] = (acc[usage.username] || 0) + (Number(usage.point) || 0);
      return acc;
    }, {});
    return Object.entries(userTotals).map(([user, total], i) => ({
      id: i,
      username: user,
      point: total
    })).sort((a, b) => b.point - a.point);
  }, [timedUsages]);

  const donutData = useMemo(() => {
    const colors = [mc.scope2.accent, mc.chain.accent, mc.scope1.accent, mc.scope3.accent, mc.cost.accent, mc.gwp.accent];
    return sectionUsages.sort((a, b) => b.point - a.point).slice(0, 6).map((s, i) => ({
      label: s.section_name,
      value: s.point,
      color: colors[i % colors.length],
      percent: totalPoints > 0 ? (s.point / totalPoints) * 100 : 0
    }));
  }, [sectionUsages, totalPoints, mc]);

  const topSections = useMemo(() => sectionUsages.sort((a, b) => b.point - a.point).slice(0, 4), [sectionUsages]);

  const topUser = useMemo(() => {
    return userUsages.length > 0 ? userUsages[0].username : '—';
  }, [userUsages]);

  useEffect(() => {
    if (setGlobalStats) {
      setGlobalStats({
        capacity: timedUsages.reduce((sum, curr) => sum + (Number(curr.point) || 0), 0).toLocaleString(),
        modules: colleagues.length,
        sync: topUser,
        totalEntries: timedUsages.length
      });
    }
  }, [colleagues.length, topUser, timedUsages, setGlobalStats]);

  const userColDefs = useMemo(() => [
    {
      headerName: t("Colleague"),
      field: "username",
      cellStyle: { ...tok.gridCellText, fontWeight: 500, ...leftAlignColDef.cellStyle, cursor: 'pointer' },
      ...defaultColDef,
      flex: 1,
      minWidth: 200,
      cellRenderer: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 24, height: 24, borderRadius: 1,
            bgcolor: selectedUser === p.value ? tok.bg.tabActive : alpha(mc.chain.accent, 0.1),
            color: selectedUser === p.value ? '#fff' : mc.chain.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.6rem'
          }}>
            {p.value?.charAt(0).toUpperCase()}
          </Box>
          <Typography variant="body2" sx={{
            ...tok.gridCellText,
            fontWeight: 700,
            color: selectedUser === p.value ? tok.bg.tabActive : 'inherit'
          }}>
            {p.value}
          </Typography>
        </Box>
      )
    },
    {
      headerName: t("Points"),
      field: "point",
      ...rightAlignColDef,
      ...defaultColDef,
      flex: 1,
      cellStyle: { ...tok.gridCellText, ...rightAlignColDef.cellStyle },
      valueFormatter: (p) => `${Number(p.value).toLocaleString()}`
    }
  ], [mc, tok, selectedUser, t]);

  const auditColDefs = useMemo(() => [
    {
      headerName: t("User"),
      field: "username",
      cellStyle: { fontWeight: 600, ...leftAlignColDef.cellStyle },
      ...defaultColDef,
      cellRenderer: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: alpha(tok.bg.tabActive, 0.1), color: tok.bg.tabActive, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.7rem' }}>
            {p.value?.charAt(0).toUpperCase()}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '13px' }}>{p.value}</Typography>
        </Box>
      )
    },
    {
      headerName: t("Section"),
      field: "section_name",
      ...leftAlignColDef, ...defaultColDef,
      cellRenderer: (p) => {
        return (
          <Typography sx={tok.gridCellText}>
            {t(p.value)}
          </Typography>
        );
      }
    },
    { headerName: t("Activity"), field: "endpoint_name", ...leftAlignColDef, ...defaultColDef, cellStyle: { ...tok.gridCellText, color: tok.text.muted, ...leftAlignColDef.cellStyle } },
    { headerName: t("Timestamp"), field: "timestamp", valueFormatter: (p) => dayjs(p.value).format('DD MMM YYYY HH:mm'), ...rightAlignColDef, ...defaultColDef, cellStyle: { ...tok.gridCellText, opacity: 0.7, ...rightAlignColDef.cellStyle } },
    {
      headerName: t("Points"),
      field: "point",
      ...rightAlignColDef, ...defaultColDef,
      cellStyle: {
        ...tok.gridCellText,
        ...rightAlignColDef.cellStyle
      },
      valueFormatter: (p) => `+${Number(p.value).toLocaleString()}`
    }
  ], [tok, mc, t]);

  const handleUpdateLimit = async (user, newLimit) => {
    setLimitLoading(true);
    try {
      const updatedUser = { ...user, usage_limit: Number(newLimit) };
      await User.update_colleague(updatedUser);
      setLocalRefresh(p => p + 1);
      setExpandedUser(null);
    } catch (err) { console.error("Update failed", err); }
    finally { setLimitLoading(false); }
  };

  const handleGridReady = (params) => {
    params.api.sizeColumnsToFit();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap, animation: 'fadeIn 0.6s ease-out' }}>

      {/* ══════════════════════════════════════════════════════════════
          1. HEADER SUMMARY & FILTERS
      ══════════════════════════════════════════════════════════════ */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: tok.sectionHeading.titlePb,
          borderBottom: tok.sectionHeading.borderBottom
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Typography sx={{
              ...tok.sectionHeading, borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`, pl: 1, borderBottom: 'none'
            }}>
              {t("Organization Usage Dashboard")}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: '160px' }}><DateSelector label="" value={startDate} onChange={setStartDate} /></Box>
              <Box sx={{ width: 8, height: '2px', bgcolor: tok.border.card, borderRadius: 1 }} />
              <Box sx={{ width: '160px' }}><DateSelector label="" value={endDate} onChange={setEndDate} /></Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%' }}>
          {isLoading ? (
            Array.from(new Array(4)).map((_, i) => (
              <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                <Paper sx={{ p: ORG_PALETTE.spacing.chartPad, width: 1, height: '140px', borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card, bgcolor: tok.bg.card }}>
                  <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
                  <Skeleton width="60%" height={32} />
                  <Skeleton width="40%" />
                </Paper>
              </Box>
            ))
          ) : (
            topSections.map((s, i) => {
              const typeKey = ['chain', 'scope2', 'scope1', 'scope3'][i % 4];
              return (
                <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                  <MetricCard
                    title={t(s.section_name)}
                    value={s.point}
                    unit="pts"
                    icon={getModuleIcon(s.section_name)}
                    type={typeKey}
                    bg={mc[typeKey].bg}
                    delay={i * 0.1}
                  />
                </Box>
              )
            })
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: tok.spacing.pageGap, flexWrap: { xs: 'wrap', lg: 'nowrap' }, width: '100%' }}>
          {/* LEFT: UNIFIED ANALYTICS */}
          <Box sx={{
            flex: '1 1 0%', minWidth: { xs: '100%', lg: '450px' },
            display: 'flex', flexDirection: 'column',
            bgcolor: tok.bg.card, borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card,
            boxShadow: tok.shadow.card, height: '400px'
          }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: tok.border.card, display: 'flex', alignItems: 'center' }}>
              <Typography sx={{
                ...tok.sectionHeading, borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`, pl: 1, textTransform: 'none', lineHeight: 1, borderBottom: 'none'
              }}>
                {t("Module Consumption Distribution")}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: tok.spacing.pageGap, p: 3 }}>
              {isLoading ? (
                <>
                  <Skeleton variant="circular" width={230} height={230} sx={{ flexShrink: 0 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton width="100%" height={20} sx={{ mb: 2 }} />
                    <Skeleton width="100%" height={20} sx={{ mb: 2 }} />
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', width: { xs: '100%', lg: 'auto' } }}>
                    {donutData.length > 0 ? (
                      <DonutChart
                        data={donutData}
                        size={230}
                        strokeWidth={30}
                        centerContent={
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ ...cellTextSx(isDark), display: 'block', color: isDark ? '#94a3b8' : '#64748b', fontSize: GRAPH_PALETTE.font.sizes.donutTotalSmall }}>
                              {t("Total")}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: GRAPH_PALETTE.font.weights.heavy, color: tok.text.primary, lineHeight: 1.2, fontSize: '28px' }}>
                              {totalPoints.toLocaleString()}
                            </Typography>
                            <Typography sx={{ ...cellTextSx(isDark), color: isDark ? '#94a3b8' : '#64748b', fontSize: GRAPH_PALETTE.font.sizes.donutTotalSmall }}>
                              {t("Points")}
                            </Typography>
                          </Box>
                        }
                      />
                    ) : (
                      <Box sx={{ width: 230, height: 230, borderRadius: '50%', border: '4px dashed', borderColor: tok.border.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ ...cellTextSx(isDark), fontWeight: GRAPH_PALETTE.font.weights.medium }}>{t("Inconclusive Data")}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                    {donutData.map((d, i) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                            <Typography sx={{
                              fontFamily: GRAPH_PALETTE.font.family,
                              fontSize: GRAPH_PALETTE.font.sizes.axisLabel,
                              fontWeight: GRAPH_PALETTE.font.weights.standard,
                              color: tok.text.primary
                            }}>
                              {t(d.label)}
                            </Typography>
                          </Box>
                          <Typography sx={{
                            fontFamily: GRAPH_PALETTE.font.family,
                            fontSize: GRAPH_PALETTE.font.sizes.axisLabel,
                            fontWeight: GRAPH_PALETTE.font.weights.standard,
                            color: tok.text.primary
                          }}>
                            {d.value.toLocaleString()} ({Math.round(d.percent)}%)
                          </Typography>
                        </Box>
                        <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                          <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* RIGHT: DRIVER HUB (TEAM LIST) */}
          <Box sx={{
            flex: '1 1 0%', minWidth: { xs: '100%', lg: '350px' },
            bgcolor: tok.bg.card, borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card,
            boxShadow: tok.shadow.card, height: '400px', display: 'flex', flexDirection: 'column'
          }}>
            <Box sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: tok.border.card,
              display: 'flex',
              alignItems: 'center'
            }}>
              <Typography sx={{
                ...tok.sectionHeading,
                borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`,
                pl: 1,
                textTransform: 'none',
                lineHeight: 1,
                borderBottom: 'none'
              }}>
                {t("Team Drivers")}
              </Typography>
              {selectedUser && (
                <Button
                  size="small"
                  onClick={() => setSelectedUser(null)}
                  sx={{
                    ml: 'auto',
                    color: tok.bg.tabActive,
                    fontWeight: 600,
                    fontSize: '13px',
                    textTransform: 'capitalize',
                    minWidth: 0,
                    p: '2px 8px',
                    '&:hover': { bgcolor: alpha(tok.bg.tabActive, 0.08) }
                  }}
                >
                  {t("Clear Filters")}
                </Button>
              )}
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden', width: '100%' }}>
              <AgGrid
                columnDefs={userColDefs}
                rowData={userUsages}
                onRowClicked={(p) => setSelectedUser(p.data.username)}
                domLayout="normal"
                pagination={false}
                onGridReady={handleGridReady}
              />
            </Box>
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════════
            2. THE "EVIDENCE" - AUDIT LOG
        ══════════════════════════════════════════════════════════════ */}
        <Box sx={{ border: '1px solid', borderColor: tok.border.card, borderRadius: tok.radius.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card, overflow: 'hidden' }}>
          <TableHeaderContainer>
            <TableTitleContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TbShieldCheck size={20} color={tok.bg.tabActive} />
                <Typography variant="Table Title">{t("System Integrity Audit")} {selectedUser ? `- ${selectedUser}` : ''}</Typography>
              </Box>
            </TableTitleContainer>
            <TableHeaderToolsContainer>
              <SearchBox text={auditSearch} setText={setAuditSearch} placeholder={t("Trace activities...")} width={300} />
              <ExportAsXLSXButton data={filteredUsages} filename="company_integrity_audit.xlsx" />
              <RefreshButton setRefresh={() => setLocalRefresh(p => p + 1)} />
            </TableHeaderToolsContainer>
          </TableHeaderContainer>
          <Box sx={{ p: 0 }}>
            <AgGrid
              columnDefs={auditColDefs}
              rowData={filteredUsages}
              quickFilterText={auditSearch}
              domLayout="autoHeight"
              pagination={true}
              footerLeftContent={
                <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                  <CheckBox checked={globalView} onChange={(e) => setGlobalView(e.target.checked)} label={t("Global Insights")} />
                </Box>
              }
            />
          </Box>
        </Box>

      {/* GLOBAL VIEW OVERLAY */}
      <Box sx={{ display: globalView ? "block" : "none" }}>
        <UsageGlobalView
          usages={filteredUsages}
          sectionUsages={sectionUsages}
          totalPoints={totalPoints}
          entityLabel={selectedUser || t("Organization")}
          accentColor={tok.bg.tabActive}
        />
      </Box>

      <style jsx="true">{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </Box>
  );
};

export default UsageCompany;
